import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Monogram } from "@/components/brand/Logo";
import { SpecialistNote } from "@/components/brand/Specialist";
import { api, errMsg } from "@/lib/api";
import { flow, getSessionId } from "@/lib/flow";
import { getUtm, track } from "@/lib/tracking";

const MESSAGES = ["Leggiamo le tue risposte", "Riconosciamo il tuo tipo di pelle", "Individuiamo le sue esigenze", "Componiamo il tuo rituale"];
const MIN_MS = 2600;

export default function Analysis() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const ran = useRef(false);

  const run = async () => {
    setError(null);
    const t0 = Date.now();
    try {
      const { data } = await api.post("/advisor/generate", { session_id: getSessionId(), answers: flow.answers(), utm: getUtm() });
      await new Promise((r) => setTimeout(r, Math.max(0, MIN_MS - (Date.now() - t0))));
      flow.setResult(data);
      flow.setSelection({ day: {}, night: {} });
      track("profile_generated", { skin: data.profile.skin_type?.key, deduced: data.profile.skin_type?.deduced });
      track("routine_generated", { day_steps: data.routine.day.length, night_steps: data.routine.night.length });
      navigate("/profilo", { replace: true });
    } catch (e) { setError(errMsg(e)); }
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!Object.keys(flow.answers()).length) { navigate("/", { replace: true }); return; }
    run();
    const iv = setInterval(() => setStep((s) => Math.min(s + 1, MESSAGES.length - 1)), MIN_MS / MESSAGES.length);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F2ECE4] flex flex-col items-center justify-center px-6 text-center" data-testid="analysis-page">
      <div className="relative w-[120px] h-[120px] flex items-center justify-center">
        <svg className="absolute inset-0" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="46" fill="none" stroke="#E6DFD5" strokeWidth="0.6" />
          <circle cx="50" cy="50" r="46" fill="none" stroke="#C5A059" strokeWidth="0.8" className="fs-ring" transform="rotate(-90 50 50)" />
        </svg>
        <Monogram size={84} className="fs-pulse border-transparent" />
      </div>
      <SpecialistNote text="Sto leggendo le tue risposte con attenzione: un attimo e ti racconto cosa ho capito della tua pelle." testid="analysis-specialist-note" className="mt-10 text-left" />
      <div className="h-16 mt-8">
        {error ? (
          <div data-testid="analysis-error">
            <p className="font-serif text-xl">{error}</p>
            <button data-testid="analysis-retry-btn" className="fs-btn mt-6" onClick={run}>Riprova</button>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.p key={step} data-testid="analysis-message" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="font-serif italic text-2xl text-[#1A1A18]">
              {MESSAGES[step]}…
            </motion.p>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
